"use client";

import { createLogger } from "@/lib/logger";
import type { OfferMode, UserIntentSegment } from "@/lib/paywall/user-intent";
import {
  CheckoutAttempt,
  CheckoutStage,
  PostHogCaptureClient,
  captureCheckoutTelemetry,
  createCheckoutAttempt,
} from "@/lib/revenuecat/checkout-telemetry";
import { getCustomerInfo, hasActiveRevenueCatAccess } from "@/lib/revenuecat/client";
import type { PackageType } from "@/lib/revenuecat/config";
import { normalizeRevenueCatPurchaseError } from "@/lib/revenuecat/purchase-errors";
import { getPurchaseErrorMessage } from "@/lib/revenuecat/user-facing-errors";
import { useCallback, useRef, useState } from "react";

const logger = createLogger("RevenueCatCheckoutAttempt");

export type CheckoutStatus =
  | "idle"
  | "preparing"
  | "awaiting_checkout"
  | "pending_unconfirmed"
  | "verifying"
  | "succeeded"
  | "cancelled"
  | "failed"
  | "unconfirmed";

type CheckoutState = {
  status: CheckoutStatus;
  locked: boolean;
  selectedPackage: PackageType | null;
};

const INITIAL_STATE: CheckoutState = {
  status: "idle",
  locked: false,
  selectedPackage: null,
};

export type CheckoutResolution = {
  status: "active" | "inactive" | "unknown" | "cancelled" | "failed" | "stale";
  message: string | null;
};

const PENDING_INACTIVE_MESSAGE =
  "We haven't confirmed your purchase yet. Keep the checkout open if it is still visible, and don't start another purchase.";
const VERIFICATION_UNKNOWN_MESSAGE =
  "We could not verify the purchase yet. Please wait a moment and check again.";
const TERMINAL_UNKNOWN_MESSAGE =
  "We couldn't verify whether the purchase completed. Check your purchase status before trying again.";

export function useRevenueCatCheckoutAttempt(posthog?: PostHogCaptureClient | null) {
  const activeAttemptRef = useRef<CheckoutAttempt | null>(null);
  const verificationRef = useRef<{
    attemptId: string;
    promise: Promise<"active" | "inactive" | "unknown">;
  } | null>(null);
  const [state, setState] = useState<CheckoutState>(INITIAL_STATE);

  const beginAttempt = useCallback(
    (input: {
      packageType: PackageType;
      offerMode: OfferMode;
      userIntentSegment: UserIntentSegment;
      surface: CheckoutAttempt["surface"];
      analyticsProperties?: Record<string, unknown>;
    }) => {
      if (activeAttemptRef.current) return null;

      const attempt = createCheckoutAttempt(input);
      activeAttemptRef.current = attempt;
      setState({
        status: "preparing",
        locked: true,
        selectedPackage: input.packageType,
      });
      return attempt;
    },
    []
  );

  const isCurrentAttempt = useCallback(
    (attemptId: string) => activeAttemptRef.current?.id === attemptId,
    []
  );
  const getCurrentAttempt = useCallback(() => activeAttemptRef.current, []);

  const transitionAttempt = useCallback(
    (
      attemptId: string,
      status: Exclude<CheckoutStatus, "idle" | "succeeded" | "cancelled" | "failed">
    ) => {
      const attempt = activeAttemptRef.current;
      if (!attempt || attempt.id !== attemptId) return false;

      if (status === "pending_unconfirmed") {
        attempt.hasBeenPending = true;
      }
      setState({
        status,
        locked: true,
        selectedPackage: attempt.packageType,
      });
      return true;
    },
    []
  );

  const finishAttempt = useCallback(
    (attemptId: string, status: "succeeded" | "cancelled" | "failed") => {
      const attempt = activeAttemptRef.current;
      if (!attempt || attempt.id !== attemptId) return false;

      activeAttemptRef.current = null;
      setState({
        status,
        locked: false,
        selectedPackage: null,
      });
      return true;
    },
    []
  );

  const verifyAttempt = useCallback(
    (attempt: CheckoutAttempt, trigger: "automatic_pending" | "manual" | "terminal_error") => {
      if (verificationRef.current?.attemptId === attempt.id) {
        return verificationRef.current.promise;
      }

      if (!transitionAttempt(attempt.id, "verifying")) {
        return Promise.resolve("unknown" as const);
      }

      const promise = getCustomerInfo()
        .then((customerInfo) => {
          const result = hasActiveRevenueCatAccess(customerInfo) ? "active" : "inactive";
          captureCheckoutTelemetry(
            posthog,
            "subscription_checkout_verification_completed",
            attempt,
            "entitlement_verification",
            {
              verificationResult: result,
              verificationTrigger: trigger,
            }
          );
          return result;
        })
        .catch((verificationError: unknown) => {
          logger.warn("Could not verify checkout", { error: verificationError });
          captureCheckoutTelemetry(
            posthog,
            "subscription_checkout_verification_completed",
            attempt,
            "entitlement_verification",
            {
              verificationResult: "unknown",
              verificationTrigger: trigger,
            }
          );
          return "unknown" as const;
        })
        .finally(() => {
          if (verificationRef.current?.promise === promise) {
            verificationRef.current = null;
          }
        });

      verificationRef.current = { attemptId: attempt.id, promise };
      return promise;
    },
    [posthog, transitionAttempt]
  );

  const startPurchase = useCallback(
    (attempt: CheckoutAttempt) => {
      if (!transitionAttempt(attempt.id, "awaiting_checkout")) return false;
      captureCheckoutTelemetry(posthog, "subscription_checkout_started", attempt, "sdk_purchase");
      return true;
    },
    [posthog, transitionAttempt]
  );

  const finishSuccessfulPurchase = useCallback(
    (attempt: CheckoutAttempt) => {
      attempt.sdkSettled = true;
      if (!finishAttempt(attempt.id, "succeeded")) return false;
      captureCheckoutTelemetry(
        posthog,
        "subscription_checkout_client_succeeded",
        attempt,
        "sdk_purchase"
      );
      return true;
    },
    [finishAttempt, posthog]
  );

  const handlePendingPurchase = useCallback(
    async (attempt: CheckoutAttempt): Promise<CheckoutResolution> => {
      if (!transitionAttempt(attempt.id, "pending_unconfirmed")) {
        return { status: "stale", message: null };
      }

      captureCheckoutTelemetry(posthog, "subscription_checkout_pending", attempt, "sdk_purchase");
      const result = await verifyAttempt(attempt, "automatic_pending");

      if (!isCurrentAttempt(attempt.id)) {
        return { status: "stale", message: null };
      }
      if (result === "active") {
        return { status: "active", message: null };
      }

      transitionAttempt(attempt.id, "unconfirmed");
      return {
        status: result,
        message: result === "inactive" ? PENDING_INACTIVE_MESSAGE : VERIFICATION_UNKNOWN_MESSAGE,
      };
    },
    [isCurrentAttempt, posthog, transitionAttempt, verifyAttempt]
  );

  const verifyCurrentAttempt = useCallback(async (): Promise<CheckoutResolution> => {
    const attempt = activeAttemptRef.current;
    if (!attempt) return { status: "stale", message: null };

    const result = await verifyAttempt(attempt, "manual");
    if (!isCurrentAttempt(attempt.id)) {
      return { status: "stale", message: null };
    }
    if (result === "active") {
      return { status: "active", message: null };
    }
    if (result === "inactive" && attempt.sdkSettled) {
      finishAttempt(attempt.id, "failed");
      return {
        status: "failed",
        message: getPurchaseErrorMessage(
          new Error("Purchase was not confirmed"),
          attempt.terminalErrorCategory ?? "unknown"
        ),
      };
    }

    transitionAttempt(attempt.id, "unconfirmed");
    return {
      status: result,
      message: result === "inactive" ? PENDING_INACTIVE_MESSAGE : VERIFICATION_UNKNOWN_MESSAGE,
    };
  }, [finishAttempt, isCurrentAttempt, transitionAttempt, verifyAttempt]);

  const handlePurchaseError = useCallback(
    async (
      attempt: CheckoutAttempt,
      stage: CheckoutStage,
      error: unknown
    ): Promise<CheckoutResolution> => {
      if (!isCurrentAttempt(attempt.id)) {
        return { status: "stale", message: null };
      }

      const normalizedError = normalizeRevenueCatPurchaseError(error);
      attempt.sdkSettled = stage === "sdk_purchase";
      attempt.terminalErrorCategory = normalizedError.category;
      logger.error("Purchase attempt ended with an error", {
        errorCategory: normalizedError.category,
        errorCode: normalizedError.errorCode,
        statusCode: normalizedError.statusCode,
        backendErrorCode: normalizedError.backendErrorCode,
        stage,
        packageType: attempt.packageType,
        surface: attempt.surface,
      });

      if (normalizedError.isCancelled) {
        captureCheckoutTelemetry(posthog, "subscription_checkout_cancelled", attempt, stage, {
          error: normalizedError,
        });
        finishAttempt(attempt.id, "cancelled");
        return {
          status: "cancelled",
          message: getPurchaseErrorMessage(error, normalizedError.category),
        };
      }

      captureCheckoutTelemetry(posthog, "subscription_purchase_failed", attempt, stage, {
        error: normalizedError,
      });

      if (stage === "sdk_purchase" && normalizedError.requiresVerification) {
        const verificationResult = await verifyAttempt(attempt, "terminal_error");
        if (!isCurrentAttempt(attempt.id)) {
          return { status: "stale", message: null };
        }
        if (verificationResult === "active") {
          return { status: "active", message: null };
        }
        if (verificationResult === "unknown") {
          transitionAttempt(attempt.id, "unconfirmed");
          return { status: "unknown", message: TERMINAL_UNKNOWN_MESSAGE };
        }
      }

      finishAttempt(attempt.id, "failed");
      return {
        status: "failed",
        message: getPurchaseErrorMessage(error, normalizedError.category),
      };
    },
    [finishAttempt, isCurrentAttempt, posthog, transitionAttempt, verifyAttempt]
  );

  return {
    ...state,
    beginAttempt,
    finishAttempt,
    finishSuccessfulPurchase,
    getCurrentAttempt,
    handlePendingPurchase,
    handlePurchaseError,
    isCurrentAttempt,
    startPurchase,
    transitionAttempt,
    verifyCurrentAttempt,
  } as const;
}
